import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { formatCurrency, formatPercentage, cn } from '@/lib/utils';
import {
  BarChart3, TrendingUp, TrendingDown, Package, AlertTriangle,
  DollarSign, Target, ArrowUpRight, Activity, ShoppingBag
} from 'lucide-react';
import { Link } from 'react-router-dom';

function useDashboardBI() {
  return useQuery({
    queryKey: ['bi-dashboard'],
    queryFn: async () => {
      const res = await api.get('/financeiro/dashboard');
      return res.data;
    },
    refetchInterval: 5 * 60 * 1000 // 5 min
  });
}

function useRelatorioProdutos() {
  return useQuery({
    queryKey: ['bi-relatorio-produtos'],
    queryFn: async () => {
      const res = await api.get('/financeiro/relatorio-produtos');
      return res.data as Array<{
        id: string; nome: string; skuInterno: string;
        precoVenda: number; custoRealTotal: number;
        estoqueDisponivel: number; margemLucroPercent: number; lucroPorPeca: number;
      }>;
    }
  });
}

export function RelatorioBi() {
  const { data: bi, isLoading: loadingBi } = useDashboardBI();
  const { data: produtos, isLoading: loadingProdutos } = useRelatorioProdutos();
  const [abaAtiva, setAbaAtiva] = useState<'resumo' | 'produtos' | 'estoque'>('resumo');

  const variacaoFat = bi?.resumo?.variacaoFaturamento || 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-primary" />
            Business Intelligence
          </h1>
          <p className="text-gray-500">Análise detalhada de lucratividade, estoque e desempenho</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-xl w-fit">
        {[
          { key: 'resumo', label: 'Resumo Financeiro', icon: Activity },
          { key: 'produtos', label: 'Lucratividade', icon: Target },
          { key: 'estoque', label: 'Estoque Crítico', icon: AlertTriangle },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setAbaAtiva(tab.key as typeof abaAtiva)}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
              abaAtiva === tab.key ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
            )}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* === ABA: RESUMO FINANCEIRO === */}
      {abaAtiva === 'resumo' && (
        <div className="space-y-6">
          {loadingBi ? (
            <div className="grid gap-4 md:grid-cols-4 animate-pulse">
              {[1,2,3,4].map(i => <div key={i} className="h-32 bg-gray-100 rounded-2xl" />)}
            </div>
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <Card className="border-gray-100">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <div className="p-2 bg-primary/10 rounded-xl">
                        <DollarSign className="w-5 h-5 text-primary" />
                      </div>
                      <span className={cn(
                        'text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1',
                        variacaoFat >= 0 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                      )}>
                        {variacaoFat >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                        {Math.abs(variacaoFat).toFixed(1)}%
                      </span>
                    </div>
                    <p className="text-2xl font-black text-gray-900">{formatCurrency(bi?.resumo?.faturamentoMes || 0)}</p>
                    <p className="text-sm text-gray-500 mt-1">Faturamento do Mês</p>
                  </CardContent>
                </Card>

                <Card className="border-gray-100">
                  <CardContent className="p-6">
                    <div className="p-2 bg-emerald-50 rounded-xl w-fit mb-3">
                      <TrendingUp className="w-5 h-5 text-emerald-600" />
                    </div>
                    <p className="text-2xl font-black text-gray-900">{formatCurrency(bi?.resumo?.lucroMes || 0)}</p>
                    <p className="text-sm text-gray-500 mt-1">Lucro Líquido</p>
                    <p className="text-xs font-bold text-emerald-600 mt-1">
                      Margem: {formatPercentage(bi?.resumo?.margem || 0)}
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-gray-100">
                  <CardContent className="p-6">
                    <div className="p-2 bg-amber-50 rounded-xl w-fit mb-3">
                      <Package className="w-5 h-5 text-amber-600" />
                    </div>
                    <p className="text-2xl font-black text-gray-900">{formatCurrency(bi?.resumo?.custoMes || 0)}</p>
                    <p className="text-sm text-gray-500 mt-1">Custo de Produção</p>
                    <p className="text-xs text-gray-400 mt-1">Custo real total do mês</p>
                  </CardContent>
                </Card>

                <Card className="border-gray-100">
                  <CardContent className="p-6">
                    <div className="p-2 bg-blue-50 rounded-xl w-fit mb-3">
                      <ShoppingBag className="w-5 h-5 text-blue-600" />
                    </div>
                    <p className="text-2xl font-black text-gray-900">
                      {(bi?.topProdutos?.length || 0)} produtos
                    </p>
                    <p className="text-sm text-gray-500 mt-1">Mais vendidos</p>
                    <p className="text-xs text-gray-400 mt-1">No período atual</p>
                  </CardContent>
                </Card>
              </div>

              <div className="grid gap-6 lg:grid-cols-2">
                {/* Top Produtos */}
                <Card className="border-gray-100">
                  <CardHeader>
                    <CardTitle className="text-base">Produtos Mais Vendidos</CardTitle>
                    <CardDescription>Ranking por faturamento no mês atual</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {bi?.topProdutos?.map((p: any, i: number) => (
                        <div key={p.produtoId} className="flex items-center gap-3">
                          <span className={cn(
                            'w-7 h-7 rounded-full flex items-center justify-center text-xs font-black shrink-0',
                            i === 0 ? 'bg-yellow-100 text-yellow-700' :
                            i === 1 ? 'bg-gray-100 text-gray-600' :
                            i === 2 ? 'bg-amber-50 text-amber-700' : 'bg-gray-50 text-gray-500'
                          )}>{i + 1}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-800 truncate">{p.nome}</p>
                            <p className="text-xs text-gray-400">{p.totalUnidades} unidades</p>
                          </div>
                          <p className="text-sm font-black text-gray-900 shrink-0">{formatCurrency(p.totalVendido)}</p>
                        </div>
                      ))}
                      {(!bi?.topProdutos || bi.topProdutos.length === 0) && (
                        <p className="text-sm text-gray-400 text-center py-4">Sem dados de vendas ainda</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Por Canal */}
                <Card className="border-gray-100">
                  <CardHeader>
                    <CardTitle className="text-base">Faturamento por Canal</CardTitle>
                    <CardDescription>Distribuição das vendas por marketplace</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {bi?.porOrigem?.map((o: any) => {
                        const pct = bi.resumo.faturamentoMes > 0
                          ? (Number(o._sum.faturamentoTotal) / bi.resumo.faturamentoMes) * 100
                          : 0;
                        const colors: Record<string, string> = {
                          SITE: 'bg-blue-500', MERCADOLIVRE: 'bg-yellow-400',
                          SHOPEE: 'bg-orange-500', MANUAL: 'bg-gray-400'
                        };
                        return (
                          <div key={o.origem}>
                            <div className="flex justify-between text-sm mb-1.5">
                              <span className="font-semibold text-gray-700">{o.origem}</span>
                              <div className="flex gap-3 text-right">
                                <span className="text-gray-400 text-xs">{o._count.id} pedidos</span>
                                <span className="font-bold text-gray-900">{formatCurrency(Number(o._sum.faturamentoTotal))}</span>
                              </div>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-2">
                              <div className={cn('rounded-full h-2 transition-all', colors[o.origem] || 'bg-gray-400')} style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        );
                      })}
                      {(!bi?.porOrigem || bi.porOrigem.length === 0) && (
                        <p className="text-sm text-gray-400 text-center py-4">Sem dados de canal ainda</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </div>
      )}

      {/* === ABA: LUCRATIVIDADE === */}
      {abaAtiva === 'produtos' && (
        <Card className="border-gray-100">
          <CardHeader>
            <CardTitle>Rentabilidade por Produto</CardTitle>
            <CardDescription>Ordenado do mais lucrativo ao menos lucrativo</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingProdutos ? (
              <div className="space-y-3 animate-pulse">
                {[1,2,3,4,5].map(i => <div key={i} className="h-10 bg-gray-100 rounded-lg" />)}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left py-3 px-2 text-xs font-bold text-gray-500 uppercase">Produto</th>
                      <th className="text-right py-3 px-2 text-xs font-bold text-gray-500 uppercase">Preço Venda</th>
                      <th className="text-right py-3 px-2 text-xs font-bold text-gray-500 uppercase">Custo Real</th>
                      <th className="text-right py-3 px-2 text-xs font-bold text-gray-500 uppercase">Lucro/Peça</th>
                      <th className="text-right py-3 px-2 text-xs font-bold text-gray-500 uppercase">Margem</th>
                      <th className="text-right py-3 px-2 text-xs font-bold text-gray-500 uppercase">Estoque</th>
                      <th className="text-right py-3 px-2 text-xs font-bold text-gray-500 uppercase"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {produtos?.map((p) => (
                      <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-2">
                          <p className="font-semibold text-gray-900 text-sm">{p.nome}</p>
                          <p className="text-xs text-gray-400">{p.skuInterno}</p>
                        </td>
                        <td className="py-3 px-2 text-right text-sm font-medium text-gray-800">
                          {formatCurrency(p.precoVenda)}
                        </td>
                        <td className="py-3 px-2 text-right text-sm text-gray-500">
                          {formatCurrency(p.custoRealTotal)}
                        </td>
                        <td className="py-3 px-2 text-right text-sm font-bold text-emerald-600">
                          {formatCurrency(p.lucroPorPeca)}
                        </td>
                        <td className="py-3 px-2 text-right">
                          <span className={cn(
                            'text-xs font-black px-2 py-0.5 rounded-full',
                            p.margemLucroPercent >= 30 ? 'bg-emerald-50 text-emerald-700' :
                            p.margemLucroPercent >= 20 ? 'bg-amber-50 text-amber-700' :
                            'bg-red-50 text-red-700'
                          )}>
                            {formatPercentage(p.margemLucroPercent)}
                          </span>
                        </td>
                        <td className="py-3 px-2 text-right text-sm text-gray-600">{p.estoqueDisponivel} un</td>
                        <td className="py-3 px-2 text-right">
                          <Link to={`/admin/produtos/${p.id}/editar`}>
                            <ArrowUpRight className="w-4 h-4 text-gray-300 hover:text-primary transition-colors" />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* === ABA: ESTOQUE CRÍTICO === */}
      {abaAtiva === 'estoque' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-amber-700">
            <AlertTriangle className="w-5 h-5 shrink-0" />
            <p className="text-sm font-medium">Produtos com estoque ≤ 3 unidades precisam de atenção imediata.</p>
          </div>
          <Card className="border-gray-100">
            <CardHeader>
              <CardTitle>Produtos com Estoque Crítico</CardTitle>
              <CardDescription>Estes produtos precisam de reposição ou nova ordem de produção</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingBi ? (
                <div className="space-y-3 animate-pulse">
                  {[1,2,3].map(i => <div key={i} className="h-10 bg-gray-100 rounded-lg" />)}
                </div>
              ) : bi?.estoqueCritico?.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Package className="w-12 h-12 mx-auto mb-3 opacity-20" />
                  <p className="font-medium">Nenhum produto com estoque crítico! 🎉</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {bi?.estoqueCritico?.map((p: any) => (
                    <div key={p.id} className="flex items-center justify-between p-4 bg-red-50 border border-red-100 rounded-xl">
                      <div>
                        <p className="font-semibold text-gray-900">{p.nome}</p>
                        <p className="text-xs text-gray-400">{p.skuInterno}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <span className="text-2xl font-black text-red-600">{p.estoqueDisponivel}</span>
                          <p className="text-xs text-gray-400">de {p.estoqueMinimo} mínimo</p>
                        </div>
                        <Link
                          to={`/admin/producao`}
                          className="flex items-center gap-1.5 bg-white border border-red-200 hover:bg-red-600 hover:text-white hover:border-red-600 text-red-600 text-xs font-bold px-3 py-1.5 rounded-lg transition-all"
                        >
                          Produzir
                          <ArrowUpRight className="w-3 h-3" />
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
